import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Container,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
  Button,
} from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { IoIosArrowUp } from "react-icons/io";
import {
  getPackagePricings,
  deletePackagePricing,
} from "../../actions/packagePricingAction";
import resetBtn from "../../assets/images/reset-arrow.svg";
import { useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import FullPageLoader from "../../components/FullPageLoader/FullPageLoader";
import SweetAlert from "react-bootstrap-sweetalert";
import { toast } from "react-toastify";
import DataTable, { createTheme } from "react-data-table-component";
import InitializeLocalStorage from "../../components/PaginationHandle";

createTheme(
  "solarized",
  {
    text: {
      primary: "#000",
      secondary: "#000",
    },
    background: {
      default: "#fff",
    },
    context: {
      background: "#cb4b16",
      text: "#FFFFFF",
    },
    divider: {
      default: "#DFE0EB",
    },
    action: {
      button: "rgba(0,0,0,.54)",
      hover: "rgba(0,0,0,.08)",
      disabled: "rgba(0,0,0,.12)",
    },
  },
  "dark"
);

const PackagePricing = () => {
  const [packagePricings, setPackagePricings] = useState([]);
  const [loader, setLoader] = useState(false);
  const { admin } = useSelector((state) => state.admin);
  const currentUserRole = admin?.role;
  const [getDelete, setDelete] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [filterText, setFilterText] = useState("");
  const [selectedOption, setSelectedOption] = useState();
  const [filteredItems, setFilteredItems] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const location = useLocation();
  const { pathname } = location;

  InitializeLocalStorage(pathname, localStorage.getItem("currentURL"));

  const hideModel = () => {
    setDelete(false);
  };

  const deletePricing = async () => {
    setDelete(false);
    setLoader(true);
    try {
      let res = await deletePackagePricing(deleteId);
      if (res?.data?.success) {
        toast.success(res.data.message);
        getAllPackagePricings();
      } else {
        toast.error(res?.data?.message || "Failed to delete package pricing");
      }
    } catch (error) {
      toast.error("Error deleting package pricing");
    }
    setLoader(false);
  };

  const showModel = (id) => {
    setDeleteId(id);
    setDelete(true);
  };

  const handlePageChange = (page) => {
    getAllPackagePricings(page);
    setCurrentPage(page);
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    getAllPackagePricings(page, newPerPage);
    setPerPage(newPerPage);
  };

  const getAllPackagePricings = async (page = 1, limit = perPage) => {
    setLoader(true);
    try {
      let res = await getPackagePricings();
      if (res?.data?.success) {
        const data = res.data.data || [];
        setTotalRows(data.length);
        setPackagePricings(data);
        setFilteredItems(data);
      }
    } catch (error) {
      console.error("Error fetching package pricings:", error);
      toast.error("Error fetching package pricings");
    }
    setLoader(false);
  };

  useEffect(() => {
    getAllPackagePricings();
  }, []);

  const caseInsensitiveSort = (rowA, rowB, columnId) => {
    const getProperty = (row, columnId) => {
      switch (columnId) {
        case "Package":
          return row?.package?.toLowerCase() || "";
        case "Discount Type":
          return row?.discountType?.toLowerCase() || "";
        case "Status":
          return row?.isActive ? "active" : "inactive";
        default:
          return "";
      }
    };

    const a = getProperty(rowA, columnId);
    const b = getProperty(rowB, columnId);

    if (a > b) return 1;
    if (b > a) return -1;
    return 0;
  };

  const getDiscountDisplay = (row) => {
    if (row.discountType === "percentage") {
      return `${row.discountPercentage}%`;
    } else if (row.discountType === "fixed") {
      return `$${row.discountPrice}`;
    }
    return "N/A";
  };

  const columns = [
    {
      name: "Package",
      selector: (row) => row.package?.charAt(0).toUpperCase() + row.package?.slice(1),
      sortable: true,
      sortFunction: (a, b) => caseInsensitiveSort(a, b, "Package"),
    },
    {
      name: "Number of Consultations",
      selector: (row) => row.numberOfConsultations || "N/A",
      sortable: true,
    },
    {
      name: "Discount Type",
      selector: (row) => row.discountType?.charAt(0).toUpperCase() + row.discountType?.slice(1),
      sortable: true,
      sortFunction: (a, b) => caseInsensitiveSort(a, b, "Discount Type"),
    },
    {
      name: "Discount Value",
      selector: (row) => getDiscountDisplay(row),
      sortable: false,
    },
    {
      name: "Description",
      selector: (row) => row.description || "N/A",
      sortable: false,
      wrap: true,
    },
    {
      name: "Status",
      selector: (row) => (
        <>
          {row.isActive ? (
            <label className="label label-success m-0">Active</label>
          ) : (
            <label className="label label-danger m-0">Inactive</label>
          )}
        </>
      ),
      sortable: true,
      sortFunction: (a, b) => caseInsensitiveSort(a, b, "Status"),
    },
    {
      name: "Actions",
      selector: (row) => (
        <div className="td-actions table-actions-btns">
          <ul className="list-unstyled mb-0 d-flex justify-content-center">
            <li className="d-inline-block align-top">
              <OverlayTrigger
                overlay={<Tooltip id="tooltip-edit">Edit</Tooltip>}
                placement="left"
              >
                <Link
                  to={`/package-pricing/edit/${row._id}`}
                  type="button"
                  className="btn-link btn-icon btn btn-success"
                >
                  <FaEdit />
                </Link>
              </OverlayTrigger>
            </li>
            <li className="d-inline-block align-top">
              <OverlayTrigger
                overlay={<Tooltip id="tooltip-delete">Delete</Tooltip>}
                placement="left"
              >
                <button
                  onClick={() => showModel(row._id)}
                  type="button"
                  className="btn-link btn-icon btn btn-danger"
                >
                  <FaTrash />
                </button>
              </OverlayTrigger>
            </li>
          </ul>
        </div>
      ),
      sortable: false,
    },
  ];

  const filterDatatable = (text) => {
    setFilterText(text);
    filterSearchDatatable(selectedOption, text);
  };

  const filterSearchDatatable = (statusFilter, searchText) => {
    const filteredItemsResult = packagePricings.filter((data) => {
      const matchesStatus = !statusFilter || 
        (statusFilter === "true" && data.isActive) ||
        (statusFilter === "false" && !data.isActive);
      
      const matchesSearch = !searchText || 
        data?.package?.toLowerCase().includes(searchText.toLowerCase()) ||
        data?.discountType?.toLowerCase().includes(searchText.toLowerCase()) ||
        data?.description?.toLowerCase().includes(searchText.toLowerCase()) ||
        getDiscountDisplay(data).toLowerCase().includes(searchText.toLowerCase());

      return matchesStatus && matchesSearch;
    });
    setFilteredItems([...filteredItemsResult]);
    setSelectedOption(statusFilter);
  };

  const resetFilter = () => {
    setFilterText("");
    setFilteredItems([...packagePricings]);
    setSelectedOption("");
  };

  const toggleFilterAccordion = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  useEffect(() => {
    if (packagePricings) {
      setFilteredItems([...packagePricings]);
    }
  }, [packagePricings]);

  return (
    <>
      {getDelete ? (
        <SweetAlert
          warning
          showCloseButton
          showCancel
          confirmBtnText="Yes"
          confirmBtnBsStyle="danger"
          cancelBtnBsStyle="light"
          title="Are you sure you want to delete?"
          onConfirm={deletePricing}
          onCancel={hideModel}
          focusCancelBtn
          customClass="del-sweet-alert"
        >
          You will not be able to recover this data!
        </SweetAlert>
      ) : null}
      {loader ? (
        <FullPageLoader />
      ) : (
        <div className="pt-2 pt-md-3">
          <Container fluid>
            <Row>
              <Col md="12">
                {/* Filter Card */}
                <Card className="filter-card card">
                  <Card.Header
                    style={{ cursor: "pointer" }}
                    onClick={toggleFilterAccordion}
                  >
                    <div className="d-flex align-items-center justify-content-between table-head">
                      <Card.Title className="text-white" as="h4">
                        Filter
                      </Card.Title>
                      <div
                        style={{
                          transform: isFilterOpen ? "rotate(0deg)" : "rotate(180deg)",
                          transition: "transform 0.3s ease",
                        }}
                      >
                        <IoIosArrowUp />
                      </div>
                    </div>
                  </Card.Header>
                  {isFilterOpen && (
                    <Card.Body>
                      <Row>
                        <Col xl={4} sm={6} className="search-wrap">
                          <Form.Group>
                            <Form.Label className="d-block mb-2 text-white">
                              Search...
                            </Form.Label>
                            <Form.Control
                              type="text"
                              name="search"
                              placeholder="Search by package, type, or description"
                              value={filterText}
                              onChange={(e) => filterDatatable(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col xl={4} sm={6} className="search-wrap">
                          <Form.Group>
                            <Form.Label className="d-block mb-2 text-white">
                              Filter with status...
                            </Form.Label>
                            <Form.Control
                              as="select"
                              value={selectedOption}
                              onChange={(e) =>
                                filterSearchDatatable(e.target.value, filterText)
                              }
                            >
                              <option value="">Select Status</option>
                              <option value="true">Active</option>
                              <option value="false">Inactive</option>
                            </Form.Control>
                          </Form.Group>
                        </Col>
                        <Col xl={4} md={6}>
                          <Form.Group>
                            <label className="d-none d-sm-block mb-2 form-label">
                              &nbsp;
                            </label>
                            <div className="d-flex filter-btns-holder">
                              <button
                                type="button"
                                className="outline-button filter-reset-btn"
                                onClick={() => resetFilter()}
                              >
                                <img
                                  src={resetBtn}
                                  className="img-fluid mr-3"
                                  alt="reset"
                                />
                                Reset
                              </button>
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                    </Card.Body>
                  )}
                </Card>

                {/* DataTable Header */}
                <div className="datatable-title card d-flex flex-row justify-content-between align-items-center mb-0">
                  <h4 className="card-title">Package Pricing</h4>
                  <div className="btn-add">
                    <Link
                      className="float-sm-right btn-filled d-flex align-items-center"
                      to="/package-pricing/add"
                    >
                      <span className="add-icon mr-2">
                        <FaPlus />
                      </span>
                      Add Package Pricing
                    </Link>
                  </div>
                </div>

                {/* DataTable */}
                <div className="custom-datatable-pagination">
                  <DataTable
                    columns={columns}
                    data={filteredItems}
                    theme="solarized"
                    pagination
                    className="react-datatable"
                    progressPending={loader}
                    paginationServer={false}
                    paginationTotalRows={totalRows}
                    paginationDefaultPage={currentPage}
                    paginationPerPage={perPage}
                    onChangeRowsPerPage={handlePerRowsChange}
                    onChangePage={handlePageChange}
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      )}
    </>
  );
};

export default PackagePricing;
